import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * The first of a ladder of ever more compact treatments at which `ref`'s
 * content fits its width (spec 10.2, 10.3): tier 0 is the full treatment,
 * and while the element or a child overflows (see `overflows`) the tier
 * steps up, to at most `maxTier`. Measured live, so a label that fits is
 * never cut down on a guess. A width change starts again from 0, and so does
 * a change of `contentKey` (a badge appearing, a label changing).
 */
/** One box's content needs more than its width (content plus its own padding). */
function boxOverflows(el: HTMLElement): boolean {
  if (el.scrollWidth > el.clientWidth + 1) return true;
  /* WebKit leaves centred overflow out of scrollWidth, so measure the
     content itself against the box less its padding. */
  const range = document.createRange();
  range.selectNodeContents(el);
  const content = range.getBoundingClientRect().width;
  const cs = getComputedStyle(el);
  const pad = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
  return content + pad > el.clientWidth + 1;
}

/**
 * The element, or one of its children, needs more than its width. Children
 * are checked too: a flex item can shrink below its label and spill it into
 * its neighbour while the row itself reports no overflow.
 */
function overflows(el: HTMLElement): boolean {
  if (boxOverflows(el)) return true;
  for (const c of Array.from(el.children)) {
    if (c instanceof HTMLElement && c.clientWidth > 0 && boxOverflows(c)) return true;
  }
  return false;
}

export function useFitTier(ref: RefObject<HTMLElement | null>, maxTier: number, contentKey: string | number = ''): number {
  const [tier, setTier] = useState(0);
  const tierRef = useRef(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let raf = 0;
    let lastW = -1;
    /* Two frames after a tier change, so React has committed it before the
       next measurement. */
    const later = () => { raf = requestAnimationFrame(() => { raf = requestAnimationFrame(check); }); };
    const check = () => {
      raf = 0;
      const w = el.clientWidth;
      if (w !== lastW) {
        lastW = w;
        if (tierRef.current !== 0) { tierRef.current = 0; setTier(0); later(); return; }
      }
      if (overflows(el) && tierRef.current < maxTier) {
        tierRef.current += 1;
        setTier(tierRef.current);
        later();
      }
    };
    const ro = new ResizeObserver(() => { if (!raf) later(); });
    ro.observe(el);
    tierRef.current = 0;
    const start = setTimeout(() => { setTier(0); later(); }, 0);
    return () => { clearTimeout(start); ro.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [ref, maxTier, contentKey]);
  return tier;
}
